import { faArrowUpRightFromSquare } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { Button, ConfirmModal } from '@feathr/components';
import { useLocalUrl } from '@feathr/extender/state';

interface IPendingImportsConfirmModalProps {
  /** Whether the modal is visible. */
  opened: boolean;
  /** Called when the user dismisses the modal without publishing. */
  onClose: () => void;
  /** Called when the user chooses to publish despite the pending imports. */
  onConfirm: () => Promise<void> | void;
}

/**
 * Warns the user that the account has pending contact imports before publishing an email
 * campaign. The warning is dismissible — publishing is never blocked.
 */
function PendingImportsConfirmModal({
  opened,
  onClose,
  onConfirm,
}: Readonly<IPendingImportsConfirmModalProps>): JSX.Element {
  const { t } = useTranslation();
  const localUrl = useLocalUrl();

  const viewImportsLink = (
    <Button
      href={localUrl('/data/imports')}
      suffix={<FontAwesomeIcon icon={faArrowUpRightFromSquare} />}
      target={'_blank'}
      type={'link'}
    >
      {t('View imports')}
    </Button>
  );

  return (
    <ConfirmModal
      alertDescription={t(
        'There are currently imports in progress that may affect your campaign’s audience. Any contacts from pending imports won’t receive this email.',
      )}
      alertLinks={viewImportsLink}
      alertTitle={t('Imports are incomplete')}
      alertType={'warning'}
      cancelButtonText={t('Cancel')}
      confirmButtonText={t('Publish anyway')}
      confirmButtonType={'primary'}
      description={t('Are you sure you want to publish?')}
      onClose={onClose}
      onConfirm={onConfirm}
      opened={opened}
      t={t}
      title={t('Imports in progress')}
    />
  );
}

export default PendingImportsConfirmModal;
